import { useEffect, useRef, useState } from "react";
import { fetchSessionNotes, type WorkoutNote } from "@heraion/shared";

import type { ProgressApiFetch } from "../Progress/types";

const EMPTY: readonly WorkoutNote[] = [];

// One session's saved timeline notes for a read-only surface (#24), the twin
// of useSessionResult: the host injects its apiFetch. Empty while loading or
// when the read fails: notes are an addition to the summary, never a reason
// to hold it back.
export function useSessionNotes(
  apiFetch: ProgressApiFetch,
  sessionId: string | null,
  /** The session's updatedAt: a correction keeps the id (#38), so a new
   *  value reads the notes again. */
  version?: string,
): readonly WorkoutNote[] {
  // Labelled with the id it was read for: another id shows nothing, with no
  // effect-time reset frame.
  const [read, setRead] = useState<{ id: string; notes: readonly WorkoutNote[] } | null>(null);
  // A ref, so a host passing a fresh function literal per render can't
  // re-trigger the effect (same rule as useSessionResult).
  const apiRef = useRef(apiFetch);
  useEffect(() => {
    apiRef.current = apiFetch;
  });

  useEffect(() => {
    // A new version of the same session keeps its notes until the read lands.
    if (sessionId === null) return;
    let cancelled = false;
    fetchSessionNotes((path) => apiRef.current(path), sessionId).then(
      (got) => {
        if (!cancelled) setRead({ id: sessionId, notes: got });
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [sessionId, version]);

  return sessionId !== null && read !== null && read.id === sessionId ? read.notes : EMPTY;
}
